import { Link } from 'react-router-dom';
import type { ActiveStrategyDto } from '../../types/strategy';
import { COLORS } from '../../constants/theme';

interface StrategyGuidanceBannerProps {
  activeStrategy: ActiveStrategyDto | null;
  isLoading: boolean;
  page: 'growth' | 'military' | 'science';
  /** SF-132: mobile Buildings' per-row target pills already show the same guidance the default
   *  per-item breakdown would — `compact` collapses the banner to just the strategy name + link. */
  compact?: boolean;
  /** SF-190 Phase 3 Decision 3: opt-in — 4 of the banner's 6 real call sites already pair it with
   *  their own local "No active strategy" line, so making every caller render a fallback
   *  unconditionally would duplicate messaging on pages this spec doesn't otherwise touch. Default
   *  false preserves today's `return null` for every caller except the one that opts in
   *  (`MobileBuildingsShell.tsx`). */
  showFallback?: boolean;
}

export function StrategyGuidanceBanner({ activeStrategy, isLoading, page, compact = false, showFallback = false }: StrategyGuidanceBannerProps) {
  if (isLoading) return null;

  if (!activeStrategy || !activeStrategy.settings) {
    if (!showFallback) return null;
    return (
      <div className={compact ? 'rounded px-3 py-2' : 'rounded mb-4 px-4 py-3'} style={{ color: COLORS.text.tertiary }}>
        <span className="text-xs">
          No active strategy —{' '}
          <Link to="/strategy" className="underline" style={{ color: COLORS.link }}>
            Set a Strategy →
          </Link>
        </span>
      </div>
    );
  }

  const { settings, name } = activeStrategy;

  return (
    <div
      className={compact ? 'rounded px-3 py-2' : 'rounded mb-4 px-4 py-3'}
      style={{
        backgroundColor: 'rgba(107, 144, 224, 0.08)',
        border: `1px solid rgba(107, 144, 224, 0.3)`,
      }}
    >
      <div className={`flex items-center justify-between ${compact ? '' : 'mb-1'}`}>
        <span
          className="text-xs uppercase tracking-wider font-medium"
          style={{ color: COLORS.link }}
        >
          Active Strategy: {name}
        </span>
        <Link
          to="/strategy"
          className="text-xs hover:underline"
          style={{ color: COLORS.link }}
        >
          Open Strategy →
        </Link>
      </div>

      {!compact && page === 'growth' && <GrowthGuidance settings={settings} />}
      {!compact && page === 'military' && <MilitaryGuidance settings={settings} />}
      {!compact && page === 'science' && <ScienceGuidance settings={settings} />}
    </div>
  );
}

function GrowthGuidance({ settings }: { settings: NonNullable<ActiveStrategyDto['settings']> }) {
  const topBuildings = Object.entries(settings.buildings)
    .filter(([, pct]) => pct > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  if (topBuildings.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: COLORS.text.tertiary }}>
      {topBuildings.map(([key, pct]) => {
        const label = BUILDING_LABELS[key] || key;
        return (
          <span key={key}>
            {label}: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{pct}%</span>
          </span>
        );
      })}
    </div>
  );
}

function MilitaryGuidance({ settings }: { settings: NonNullable<ActiveStrategyDto['settings']> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: COLORS.text.tertiary }}>
      <span>Draft: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.draftLevel}%</span></span>
      <span>Rate: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.draftRate}</span></span>
      <span>Wage: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.wageRate}%</span></span>
      <span>Off: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.armyComp.offSpec}/a</span></span>
      <span>Def: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.armyComp.defSpec}/a</span></span>
      <span>Elite: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.armyComp.elite}/a</span></span>
    </div>
  );
}

function ScienceGuidance({ settings }: { settings: NonNullable<ActiveStrategyDto['settings']> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: COLORS.text.tertiary }}>
      <span>Rate: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{settings.scienceRate.replace('_', ' ')}</span></span>
      {Object.entries(settings.scienceAlloc)
        .filter(([, pct]) => pct > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 4)
        .map(([branch, pct]) => (
          <span key={branch}>
            {branch.charAt(0).toUpperCase() + branch.slice(1)}: <span className="font-mono" style={{ color: COLORS.text.secondary }}>{pct}%</span>
          </span>
        ))}
    </div>
  );
}

const BUILDING_LABELS: Record<string, string> = {
  HOME: 'Homes', FARM: 'Farms', MILL: 'Mills', BANK: 'Banks',
  TRAINING_GROUND: 'TGs', ARMOURY: 'Armouries', FORT: 'Forts',
  STABLE: 'Stables', DUNGEON: 'Dungeons', GUILD: 'Guilds',
  TOWER: 'Towers', LIBRARY: 'Libraries', SCHOOL: 'Schools',
  THIEVES_DEN: "T. Dens", WATCH_TOWER: 'Watchtowers',
  GUARD_STATION: 'Guard St.', MILITARY_BARRACK: 'Barracks',
  HOSPITAL: 'Hospitals',
};
